/**
 * Wyprowadzanie KANAŁU ruchu (źródło + medium) — wspólne dla backendu i frontu.
 *
 * VP-243, dwa niezależne problemy z tego samego zgłoszenia:
 *
 * B) Landingi dostają URL BEZ parametrów `utm_*` — tylko `fbclid`, `fbc_id`, `h_ad_id`
 *    i referrer `m.facebook.com`. Źródło czytaliśmy wyłącznie z `utm_*`, więc zostawało
 *    NULL mimo kompletu danych do ustalenia kanału (48% leadów i 40% rozmów bez źródła).
 *
 * C) Tam, gdzie `utm_*` są, szablon w kampaniach ma makra w złych slotach:
 *    `utm_source` = nazwa kampanii ("viral.partnerts | bidcap | tof | autowebinar 5 |
 *    6.07.2026"), `utm_medium` = nazwa ad setu, `utm_campaign` = nazwa reklamy.
 *    Efekt: filtr „Źródło" zamiast kilku kanałów dostawał setki nazw kampanii.
 *
 * Reguła: `utm_source` bierzemy tylko, gdy wygląda na kanał. W przeciwnym razie
 * wyprowadzamy kanał z click-idów i referrera. Nazwa kampanii nie znika — zostaje
 * w `utm_campaign`/`utm_medium`, gdzie wpisał ją szablon klienta.
 */

/** Aliasy platform → postać kanoniczna. Bez tego filtr widzi „fb" i „facebook" osobno. */
const SOURCE_ALIASES: Record<string, string> = {
  fb: "facebook",
  "facebook.com": "facebook",
  "m.facebook.com": "facebook",
  "l.facebook.com": "facebook",
  "lm.facebook.com": "facebook",
  meta: "facebook",
  ig: "instagram",
  "instagram.com": "instagram",
  "l.instagram.com": "instagram",
  yt: "youtube",
  "youtube.com": "youtube",
  "m.youtube.com": "youtube",
  tt: "tiktok",
  "tiktok.com": "tiktok",
  googleads: "google",
  "google-ads": "google",
  gads: "google",
  "google.com": "google",
  "linkedin.com": "linkedin",
  "lnkd.in": "linkedin",
};

/** Hosty referrera → kanał. Dopasowanie po zawieraniu, więc łapie subdomeny. */
const REFERRER_HOSTS: Array<[needle: string, source: string, organic: boolean]> = [
  ["facebook", "facebook", false],
  ["instagram", "instagram", false],
  ["tiktok", "tiktok", false],
  ["youtube", "youtube", false],
  ["linkedin", "linkedin", false],
  ["google", "google", true],
  ["bing", "bing", true],
  ["duckduckgo", "duckduckgo", true],
  ["yahoo", "yahoo", true],
];

/** Parametry w URL-u landinga, które zdradzają kliknięcie w reklamę Meta. */
const META_CLICK_PARAMS = ["fbclid", "fbc_id", "h_ad_id"];

export type TrafficSource = { source: string; medium: string };

/**
 * Czy wartość `utm_source` jest nazwą kampanii, a nie kanałem.
 * Sygnały: pionowe kreski (separator w nazwach kampanii Meta) albo nienaturalna długość.
 * Prawdziwe źródło to jedno słowo („facebook", „newsletter"), nigdy zdanie z datą.
 */
export function looksLikeCampaignName(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  // Same cyfry = identyfikator (na prodzie trafiło się `utm_source=120247925192420190`,
  // czyli ID reklamy), nigdy nazwa kanału.
  if (/^\d+$/.test(v)) return true;
  return v.includes("|") || v.length > 40;
}

/** Sprowadza źródło do postaci kanonicznej (lowercase + aliasy platform). */
export function canonicalSource(value: string): string {
  const v = value.trim().toLowerCase();
  return SOURCE_ALIASES[v] ?? v;
}

function hostFromUrl(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function hasMetaClickParam(pageUrl: string | null | undefined): boolean {
  if (!pageUrl) return false;
  const host = hostFromUrl(pageUrl);
  if (host === null) return false; // nie-URL — nie zgadujemy po surowym stringu
  const query = pageUrl.slice(pageUrl.indexOf("?") + 1).toLowerCase();
  return META_CLICK_PARAMS.some((p) => query.includes(`${p}=`));
}

/**
 * Kanał wyprowadzony z click-idów i referrera — gdy `utm_source` nie nadaje się na źródło.
 * Zwraca `null`, gdy nie ma z czego wyprowadzić (wejście naprawdę bezpośrednie).
 */
export function deriveTrafficSource(input: {
  referrer?: string | null;
  pageUrl?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
}): TrafficSource | null {
  const refHost = input.referrer ? hostFromUrl(input.referrer) : null;

  // Click-idy są najmocniejszym sygnałem — jednoznacznie znaczą ruch płatny.
  if (input.fbclid || hasMetaClickParam(input.pageUrl)) {
    // Instagram i Facebook dzielą fbclid; rozróżnia je dopiero referrer.
    const source = refHost?.includes("instagram") ? "instagram" : "facebook";
    return { source, medium: "paid" };
  }
  if (input.gclid || input.gbraid || input.wbraid) return { source: "google", medium: "cpc" };
  if (input.ttclid) return { source: "tiktok", medium: "paid" };
  if (input.msclkid) return { source: "bing", medium: "cpc" };

  if (refHost) {
    for (const [needle, source, organic] of REFERRER_HOSTS) {
      if (refHost.includes(needle)) {
        return { source, medium: organic ? "organic" : "referral" };
      }
    }
  }

  // Nierozpoznany host referrera NIE staje się źródłem. Dry-run backfillu na prodzie
  // pokazał, czym to grozi: 7113 konwersji dostałoby źródło "app.responso.com" (panel
  // helpdesku), 159 "oskarossowski.com" (własna domena klienta), a dalej panele Stripe,
  // BaseLinkera i webmaile. To wymyślanie kanału, którego nie było — gorsze niż puste
  // pole, bo wygląda na dane. Wyprowadzamy wyłącznie rozpoznane platformy ruchu.
  return null;
}

/**
 * Ostateczne źródło/medium do ZAPISU przy konwersji.
 *
 * `utm_source` wygrywa tylko wtedy, gdy wygląda na kanał — inaczej (brak albo nazwa
 * kampanii) schodzimy na wyprowadzenie z click-idów/referrera. Gdy i to nie da rady,
 * zwracamy `null`, czyli uczciwe „nie wiadomo" zamiast zmyślonego źródła.
 */
export function resolveTrafficSource(input: {
  utmSource?: string | null;
  utmMedium?: string | null;
  referrer?: string | null;
  pageUrl?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
}): { source: string | null; medium: string | null } {
  const raw = input.utmSource?.trim();
  if (raw && !looksLikeCampaignName(raw)) {
    // Medium przepuszczamy przez ten sam test: w rozjechanym szablonie klienta
    // `utm_medium` to nazwa ad setu ("viral.partners | TOF | AUTOWEBINAR | ..."),
    // a taka wartość nie jest medium i tylko zaśmieciłaby etykietę kanału w UI.
    const rawMedium = input.utmMedium?.trim();
    const medium = rawMedium && !looksLikeCampaignName(rawMedium) ? rawMedium.toLowerCase() : null;
    return { source: canonicalSource(raw), medium };
  }

  const derived = deriveTrafficSource(input);
  if (derived) return derived;

  return { source: null, medium: null };
}
